// frontend/src/hooks/useStorePlanning.js
//
// The store's planning defaults — the ROOT of the two chains a supplier can
// override (api.services.planning_defaults): inventory target mode + days of
// supply, and review frequency + preferred order day.
//
// The Supplier setup grid needs the root even for a row that overrides it: an
// "Store setting: Weekly" option has to name the value the merchant
// would fall back to, and the row's `effective_*` fields only carry the store
// default while the row is still inheriting. The grid also keeps a row's
// `effective_*` values current after an in-grid edit (before the save
// re-reads them) — see supplierPlanningColumns.applyPlanningEdit.
//
// Own cache key rather than `storeSettings.all` — that entry (Store settings
// page) holds `{settings, forms}`, a different shape under the same endpoint.
// Same strict reader as that page (`extractPlanningForm`): a payload missing a
// planning field fails the query rather than seeding a default the grid would
// then label "store default".

import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../contexts/AuthContext';
import { storeSettings as storeSettingsKey } from '../queries/keys';
import { sessionService } from '../services/SessionApiService';
import { extractPlanningForm } from '../pages/storeSettingsForms';

export function useStorePlanning() {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;

  const query = useQuery({
    queryKey: storeSettingsKey.planning(storeId),
    queryFn: async () => extractPlanningForm((await sessionService.getStoreSettings()).planning),
    enabled: !!storeId,
  });

  return {
    // null until loaded or when the read failed — the grid renders the
    // planning columns read-only and says so, rather than editing against a
    // root it cannot name.
    planning: query.data ?? null,
    isError: query.isError,
  };
}
